import { useEffect, useState } from "react";
import type { Hasher } from "@hash/shared";
import { isValidEmail } from "@hash/shared";
import { api } from "../api/client";
import { parseApiError } from "../api/errors";
import { startSignIn, completeSignIn } from "../auth";
import { PhoneDialog } from "../components/PhoneDialog";

interface Props {
  // Current phone from the signed-in hasher's token claims.
  phone: string;
}

/**
 * Self-service account settings. A hasher can freely edit their own metadata
 * (nerd name) and change their phone (Story 1.3, fresh-OTP gated). Replacing
 * their email is special: it requires verifying the new address with a code
 * sent to it, so they can only ever set an email they actually control.
 */
export function AccountPage({ phone }: Props) {
  const [me, setMe] = useState<Hasher | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    api.me
      .get()
      .then(setMe)
      .catch((e) => setLoadError(parseApiError(e).message));
  }, []);

  return (
    <div className="min-h-screen px-4 py-6">
      <h2 className="text-xl font-bold text-slate-900">Account</h2>

      {loadError && (
        <p className="mt-4 text-sm text-red-600" role="alert">
          {loadError}
        </p>
      )}

      <ProfileCard me={me} onSaved={setMe} />
      <EmailCard me={me} onChanged={setMe} />
      <PhoneCard phone={phone} />
    </div>
  );
}

/** Freely-editable metadata: the hash name and the nerd (real) name. */
function ProfileCard({
  me,
  onSaved,
}: {
  me: Hasher | null;
  onSaved: (h: Hasher) => void;
}) {
  const [name, setName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [nerdName, setNerdName] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Seed the inputs once the record loads.
  useEffect(() => {
    if (me) {
      setName(me.name);
      setDisplayName(me.displayName ?? "");
      setNerdName(me.nerdName ?? "");
    }
  }, [me]);

  const dirty =
    me != null &&
    (name.trim() !== me.name ||
      displayName.trim() !== (me.displayName ?? "") ||
      nerdName.trim() !== (me.nerdName ?? ""));
  const canSave = dirty && !busy && name.trim().length > 0;

  const save = async () => {
    setBusy(true);
    setStatus(null);
    setError(null);
    try {
      const updated = await api.me.updateProfile({
        name: name.trim(),
        displayName: displayName.trim(),
        nerdName: nerdName.trim(),
      });
      onSaved(updated);
      setStatus("Saved.");
    } catch (e) {
      setError(parseApiError(e, "Couldn't save").message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4 space-y-3">
      <label className="block">
        <span className="text-xs font-medium uppercase tracking-wide text-slate-400">
          Hash name
        </span>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-label="Hash name"
          disabled={!me}
          className="mt-1 h-12 w-full rounded-lg border border-slate-300 px-3 text-base focus:outline-none focus:ring-2 focus:ring-slate-900 disabled:bg-slate-50"
        />
      </label>
      <label className="block">
        <span className="text-xs font-medium uppercase tracking-wide text-slate-400">
          Display name
        </span>
        <input
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Shown on the hareline instead of your hash name"
          aria-label="Display name"
          disabled={!me}
          className="mt-1 h-12 w-full rounded-lg border border-slate-300 px-3 text-base focus:outline-none focus:ring-2 focus:ring-slate-900 disabled:bg-slate-50"
        />
      </label>
      <label className="block">
        <span className="text-xs font-medium uppercase tracking-wide text-slate-400">
          Nerd name
        </span>
        <input
          type="text"
          value={nerdName}
          onChange={(e) => setNerdName(e.target.value)}
          placeholder="Your real name"
          aria-label="Nerd name"
          disabled={!me}
          className="mt-1 h-12 w-full rounded-lg border border-slate-300 px-3 text-base focus:outline-none focus:ring-2 focus:ring-slate-900 disabled:bg-slate-50"
        />
      </label>
      <button
        onClick={save}
        disabled={!canSave}
        className="h-11 rounded-lg bg-slate-900 px-4 font-medium text-white active:bg-slate-700 disabled:opacity-50"
      >
        {busy ? "Saving…" : "Save"}
      </button>
      {status && <p className="text-sm text-emerald-700">{status}</p>}
      {error && (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Email: replacing it requires a code sent to the NEW address. Two stages —
 * enter the address (sends the code), then enter the code (stores the email).
 */
function EmailCard({
  me,
  onChanged,
}: {
  me: Hasher | null;
  onChanged: (h: Hasher) => void;
}) {
  // "idle" → showing current; "entering" → typing the new address;
  // "code" → entering the code we sent.
  const [stage, setStage] = useState<"idle" | "entering" | "code">("idle");
  const [newEmail, setNewEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setStage("idle");
    setNewEmail("");
    setCode("");
    setError(null);
  };

  const sendCode = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.me.startEmailChange(newEmail.trim());
      setStage("code");
      setStatus(null);
    } catch (e) {
      setError(parseApiError(e, "Couldn't send the code").message);
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const updated = await api.me.changeEmail(newEmail.trim(), code.trim());
      onChanged(updated);
      reset();
      setStatus("Your email was verified and updated.");
    } catch (e) {
      setError(parseApiError(e, "Couldn't verify the code").message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-slate-400">
        Email
      </div>
      <div className="mt-1 text-lg font-medium text-slate-900">
        {me?.email || "—"}
      </div>

      {stage === "idle" && (
        <button
          onClick={() => {
            setStatus(null);
            setStage("entering");
          }}
          disabled={!me}
          className="mt-4 h-11 rounded-lg bg-slate-900 px-4 font-medium text-white active:bg-slate-700 disabled:opacity-50"
        >
          {me?.email ? "Change email" : "Add email"}
        </button>
      )}

      {stage === "entering" && (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-slate-600">
            Enter the new address — we'll send a code to confirm you own it.
          </p>
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="you@example.com"
            aria-label="New email"
            autoFocus
            className="h-12 w-full rounded-lg border border-slate-300 px-3 text-base focus:outline-none focus:ring-2 focus:ring-slate-900"
          />
          <div className="flex gap-2">
            <button
              onClick={reset}
              className="h-12 flex-1 rounded-lg bg-slate-100 font-medium text-slate-700"
            >
              Cancel
            </button>
            <button
              onClick={sendCode}
              disabled={busy || !isValidEmail(newEmail.trim())}
              className="h-12 flex-1 rounded-lg bg-slate-900 font-medium text-white disabled:opacity-50"
            >
              {busy ? "Sending…" : "Send code"}
            </button>
          </div>
        </div>
      )}

      {stage === "code" && (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-slate-600">
            Enter the code we sent to{" "}
            <span className="font-medium">{newEmail.trim()}</span>.
          </p>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="123456"
            value={code}
            onChange={(e) =>
              setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
            }
            aria-label="Email verification code"
            className="h-12 w-full rounded-lg border-2 border-slate-200 px-4 text-center text-xl tracking-[0.4em] focus:border-slate-900 focus:outline-none"
          />
          <div className="flex gap-2">
            <button
              onClick={reset}
              className="h-12 flex-1 rounded-lg bg-slate-100 font-medium text-slate-700"
            >
              Cancel
            </button>
            <button
              onClick={confirm}
              disabled={busy || code.length !== 6}
              className="h-12 flex-1 rounded-lg bg-slate-900 font-medium text-white disabled:opacity-50"
            >
              {busy ? "Verifying…" : "Verify & save"}
            </button>
          </div>
        </div>
      )}

      {status && <p className="mt-3 text-sm text-emerald-700">{status}</p>}
      {error && (
        <p className="mt-3 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Phone (Story 1.3): the server requires a fresh OTP (≤5 min). If the change
 * comes back REAUTH_REQUIRED, we send a code to the *current* number, collect
 * it, then retry the change with a freshened session.
 */
function PhoneCard({ phone }: { phone: string }) {
  const [currentPhone, setCurrentPhone] = useState(phone);
  const [showDialog, setShowDialog] = useState(false);
  // The new number awaiting a re-verification before we can apply it.
  const [pendingNew, setPendingNew] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const applyChange = async (newPhone: string) => {
    const res = await api.me.changePhone(newPhone);
    setCurrentPhone(res.phone);
    setStatus("Your phone number was updated.");
    setError(null);
  };

  // Called from the dialog. Swallow REAUTH_REQUIRED to drive the re-verify
  // step; rethrow anything else so the dialog shows it inline.
  const onDialogSubmit = async (e164: string) => {
    try {
      await applyChange(e164);
      setShowDialog(false);
    } catch (err) {
      const apiErr = parseApiError(err);
      if (apiErr.code === "REAUTH_REQUIRED") {
        setShowDialog(false);
        setPendingNew(e164);
        setStatus(null);
        await startSignIn(currentPhone);
        return;
      }
      throw err;
    }
  };

  const confirmReauth = async () => {
    setBusy(true);
    setError(null);
    try {
      await completeSignIn(code);
      await applyChange(pendingNew!);
      setPendingNew(null);
      setCode("");
    } catch (err) {
      setError(parseApiError(err, "Couldn't verify the code").message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-slate-400">
        Phone number
      </div>
      <div className="mt-1 text-lg font-medium text-slate-900">
        {currentPhone || "—"}
      </div>

      {pendingNew ? (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-slate-600">
            Enter the code we sent to your current number to confirm changing it
            to <span className="font-medium">{pendingNew}</span>.
          </p>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="123456"
            value={code}
            onChange={(e) =>
              setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
            }
            aria-label="Verification code"
            className="h-12 w-full rounded-lg border-2 border-slate-200 px-4 text-center text-xl tracking-[0.4em] focus:border-slate-900 focus:outline-none"
          />
          <div className="flex gap-2">
            <button
              onClick={() => {
                setPendingNew(null);
                setCode("");
              }}
              className="h-12 flex-1 rounded-lg bg-slate-100 font-medium text-slate-700"
            >
              Cancel
            </button>
            <button
              onClick={confirmReauth}
              disabled={busy || code.length !== 6}
              className="h-12 flex-1 rounded-lg bg-slate-900 font-medium text-white disabled:opacity-50"
            >
              {busy ? "Confirming…" : "Confirm change"}
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => {
            setStatus(null);
            setError(null);
            setShowDialog(true);
          }}
          className="mt-4 h-11 rounded-lg bg-slate-900 px-4 font-medium text-white active:bg-slate-700"
        >
          Change number
        </button>
      )}

      {status && <p className="mt-3 text-sm text-emerald-700">{status}</p>}
      {error && (
        <p className="mt-3 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}

      {showDialog && (
        <PhoneDialog
          title="Change your phone number"
          hint="We'll send a code to confirm it's you, then update your login number."
          submitLabel="Continue"
          onClose={() => setShowDialog(false)}
          onSubmit={onDialogSubmit}
        />
      )}
    </div>
  );
}
